import {
  AssetTypeType,
  GetAssetRequestPathParamType,
  GetAssetRequestQueryParamType,
  GetAssetResponseBodyType,
} from "@intelligems/ig-types";
import { useLiveOrgTheme } from "@src/api/shopify-themes/get-themes";
import { QueryObserverOptions, useQuery } from "@tanstack/react-query";

import { api } from "../api";
import { useGetOrg } from "../organizations/get-organization";
import { organizationThemeAssetsKey } from "../queryKeys";
import { ApiRequest } from "../types/api";

const primaryColorKeys = [
  "checkout_button_color",
  "color_primary",
  "checkout_accent_color",
  "color_accent",
  "accent_color",
  "color_primary_button_bg_hover",
  "color_secondary_button_bg",
  "color_primary_hover",
];

const secondaryColorKeys = [
  "checkout_accent_color",
  "color_accent",
  "accent_color",
  "color_primary",
  "color_secondary_button_bg_hover",
  "color_header_text_hover",
  "color_primary_hover",
];

const getThemeAsset = async (
  payload: ApiRequest<
    any,
    GetAssetRequestPathParamType,
    GetAssetRequestQueryParamType
  >
) => {
  const resp = await api.get<GetAssetResponseBodyType>(
    `/v2/organizations/theme/${payload.pathParams.themeId}/asset`,
    {
      params: payload.queryParams,
    }
  );
  return resp.data;
};

export const useGetThemeAsset = (
  key: string,
  assetType: AssetTypeType,
  themeId?: string,
  options?: QueryObserverOptions<GetAssetResponseBodyType, Error>
) => {
  const org = useGetOrg();
  return useQuery<GetAssetResponseBodyType, Error>({
    queryKey: organizationThemeAssetsKey.one(
      org.data.org.id,
      themeId!,
      assetType
    ),
    queryFn: async () =>
      getThemeAsset({
        body: undefined,
        pathParams: { themeId: themeId! },
        queryParams: { key },
      }),
    enabled: !!themeId,
    ...options,
  });
};

export const useThemeGlobalColors = () => {
  const bar = useGetThemeAsset(
    "config/settings_data.json",
    "config",
    useLiveOrgTheme()
  );

  const settingsData = bar.data?.asset?.value
    ? JSON.parse(bar.data.asset.value)
    : null;

  const current = settingsData?.current || {};

  const findColor = (keys: string[], excludeColor?: string) => {
    for (const key of keys) {
      const color = current[key];
      if (color && color !== excludeColor) {
        return color;
      }
    }
    return "#000000";
  };

  const primary = findColor(primaryColorKeys);
  const secondary = findColor(secondaryColorKeys, primary);

  const colors = { primary, secondary };

  return {
    ...bar,
    colors,
  };
};
